import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import BookingComments from '@/components/bookings/BookingComments';
import {
  MapPin,
  Calendar,
  Building2,
  X,
  Monitor,
  Laptop,
  CalendarIcon
} from 'lucide-react';

/**
 * OverviewContent - Two-column layout with editable details (left) and comments (right)
 * Supports autosave for draft status
 */
const OverviewContent = ({ booking, onRefresh, onSaveStateChange }) => {
  const { user, userProfile, company } = useAuth();
  const [saving, setSaving] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // Course search state
  const [courseSearch, setCourseSearch] = useState(booking?.course_name || '');
  const [selectedCourseData, setSelectedCourseData] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    trainingType: booking?.delivery_methods || ['onsite'],
    trainingLocation: booking?.locations || '',
    dateType: 'flexible',
    flexibleMonths: '',
    specificDates: [],
    notes: booking?.further_info || '',
    poNumber: booking?.po_number || ''
  });

  const isEditable = booking?.status === 'draft';

  // Initialize form data from booking
  useEffect(() => {
    if (booking) {
      setCourseSearch(booking.course_name || '');
      setFormData(prev => ({
        ...prev,
        trainingType: booking.delivery_methods || ['onsite'],
        trainingLocation: booking.locations || '',
        notes: booking.further_info || '',
        poNumber: booking.po_number || ''
      }));

      // Parse date preferences
      if (booking.date_prefs) {
        const datePrefs = booking.date_prefs;
        if (datePrefs.toUpperCase() === 'ASAP') {
          setFormData(prev => ({ ...prev, dateType: 'asap' }));
        } else if (datePrefs.includes(',')) {
          try {
            const dates = datePrefs.split(',').map(d => new Date(d.trim())).filter(d => !isNaN(d));
            if (dates.length > 0) {
              setFormData(prev => ({ ...prev, dateType: 'specific', specificDates: dates }));
            } else {
              setFormData(prev => ({ ...prev, dateType: 'flexible', flexibleMonths: datePrefs }));
            }
          } catch {
            setFormData(prev => ({ ...prev, dateType: 'flexible', flexibleMonths: datePrefs }));
          }
        } else {
          setFormData(prev => ({ ...prev, dateType: 'flexible', flexibleMonths: datePrefs }));
        }
      }
    }
  }, [booking?.id]);

  // Mark as initialized after first render
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-save when form data changes
  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData, courseSearch]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCourseSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return [];

    try {
      const [apiResults, dbResults] = await Promise.all([
        fetch(
          `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?search=${searchTerm}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
            }
          }
        )
          .then(res => res.json())
          .then(response => {
            const courses = response?.data || [];
            return Array.isArray(courses) ? courses.map(course => ({
              value: course.name,
              label: course.name,
              group: 'Verified Courses',
              data: { ...course, _isVerifiedCourse: true }
            })) : [];
          })
          .catch(() => []),

        supabase
          .from('custom_courses')
          .select('*')
          .ilike('title', `%${searchTerm}%`)
          .or(`company_id.eq.${company?.id},company_id.is.null`)
          .limit(10)
          .then(({ data }) => (data || []).map(course => ({
            value: course.title || course.name,
            label: course.title || course.name,
            group: 'Custom Courses',
            data: course
          })))
      ]);

      const combined = [...apiResults, ...dbResults];
      return combined.reduce((acc, curr) => {
        if (!acc.find(item => item.value === curr.value)) acc.push(curr);
        return acc;
      }, []);
    } catch {
      return [];
    }
  };

  const handleSave = async () => {
    setSaving(true);
    onSaveStateChange?.(true);
    try {
      // Build update data - PO number is always editable
      const updateData = {
        po_number: formData.poNumber?.trim() || null,
        updated_date: new Date().toISOString()
      };

      // Only include other fields if in draft mode
      if (isEditable) {
        let datePrefs = null;
        if (formData.dateType === 'flexible' && formData.flexibleMonths?.trim()) {
          datePrefs = formData.flexibleMonths;
        } else if (formData.dateType === 'specific' && formData.specificDates.length > 0) {
          datePrefs = formData.specificDates
            .sort((a, b) => a - b)
            .map(date => format(date, 'yyyy-MM-dd'))
            .join(', ');
        } else if (formData.dateType === 'asap') {
          datePrefs = 'ASAP';
        } else if (booking?.date_prefs) {
          datePrefs = booking.date_prefs;
        }

        updateData.course_name = courseSearch;
        updateData.delivery_methods = formData.trainingType;
        updateData.locations = formData.trainingLocation;
        updateData.date_prefs = datePrefs;
        updateData.further_info = formData.notes?.trim() || null;

        if (selectedCourseData?.id) {
          updateData.course_id = selectedCourseData.id;
        }
      }

      const { error } = await supabase
        .from('training_quotes')
        .update(updateData)
        .eq('id', booking.id);

      if (error) throw error;

      const now = new Date();
      onSaveStateChange?.(false, now, null);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
      onSaveStateChange?.(false, null, null);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
      {/* Left Column - Details */}
      <div className="space-y-6">
        {/* Course Name */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Course Name</Label>
          {isEditable ? (
            <AsyncCombobox
              value={courseSearch}
              displayLabel={courseSearch}
              onChange={(value, option) => {
                setCourseSearch(value);
                if (option?.data) setSelectedCourseData(option.data);
              }}
              onSearch={handleCourseSearch}
              placeholder="Search for a course..."
              allowCustomValue={true}
            />
          ) : (
            <p className="text-gray-900 font-medium">{booking?.course_name || 'Not specified'}</p>
          )}
        </div>

        <Separator />

        {/* Delivery Method */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Delivery Type</Label>
          {isEditable ? (
            <div className="flex gap-2">
              {[
                { value: 'onsite', label: 'Onsite', icon: MapPin },
                { value: 'classroom', label: 'Classroom', icon: Building2 },
                { value: 'remote', label: 'Remote', icon: Monitor },
                { value: 'elearning', label: 'E-Learning', icon: Laptop }
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    const currentTypes = formData.trainingType || [];
                    const isSelected = currentTypes.includes(value);
                    const newTypes = isSelected
                      ? currentTypes.filter(t => t !== value)
                      : [...currentTypes, value];
                    handleInputChange('trainingType', newTypes);
                  }}
                  className={cn(
                    'flex-1 p-3 rounded-lg border-2 transition-all text-center',
                    formData.trainingType?.includes(value)
                      ? 'border-blue-400 bg-blue-50/30 shadow-lg shadow-blue-100/50'
                      : 'border-gray-200/50 bg-white/50 hover:border-gray-300/50 hover:shadow-md'
                  )}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={cn(
                        'p-2 rounded-lg transition-colors',
                        formData.trainingType?.includes(value)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div
                      className={cn(
                        'text-sm font-semibold transition-colors',
                        formData.trainingType?.includes(value)
                          ? 'text-blue-900'
                          : 'text-gray-900'
                      )}
                    >
                      {label}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {booking?.delivery_methods?.map((method, idx) => (
                <Badge key={idx} variant="outline" className="bg-blue-50 capitalize">
                  {method}
                </Badge>
              )) || <span className="text-gray-500">Not specified</span>}
            </div>
          )}
        </div>

        {/* Location - Show when onsite/classroom selected */}
        {(formData.trainingType.includes('onsite') || formData.trainingType.includes('classroom') || booking?.locations) && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Location</Label>
            {isEditable ? (
              <Input
                value={formData.trainingLocation}
                onChange={(e) => handleInputChange('trainingLocation', e.target.value)}
                placeholder="Enter training location"
              />
            ) : (
              <p className="text-gray-900">{booking?.locations || 'Not specified'}</p>
            )}
          </div>
        )}

        <Separator />

        {/* Date Preferences */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Preferred Date</Label>
          {isEditable ? (
            <div className="space-y-3">
              {/* Date Type Buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.dateType === 'flexible' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('dateType', 'flexible')}
                  className="flex-1"
                >
                  Flexible dates
                </Button>
                <Button
                  type="button"
                  variant={formData.dateType === 'specific' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('dateType', 'specific')}
                  className="flex-1"
                >
                  Selected dates
                </Button>
                <Button
                  type="button"
                  variant={formData.dateType === 'asap' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('dateType', 'asap')}
                  className="flex-1"
                >
                  ASAP
                </Button>
              </div>

              {/* Date Inputs Based on Selection */}
              {formData.dateType === 'flexible' && (
                <Input
                  value={formData.flexibleMonths}
                  onChange={(e) => handleInputChange('flexibleMonths', e.target.value)}
                  placeholder="e.g., January-March 2025"
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                />
              )}

              {formData.dateType === 'specific' && (
                <div className="space-y-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-white",
                          !formData.specificDates?.length && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.specificDates?.length > 0
                          ? `${formData.specificDates.length} date${formData.specificDates.length > 1 ? 's' : ''} selected`
                          : 'Select dates'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="multiple"
                        selected={formData.specificDates}
                        onSelect={(dates) => handleInputChange('specificDates', dates || [])}
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        initialFocus
                        className="rounded-md border"
                      />
                    </PopoverContent>
                  </Popover>

                  {formData.specificDates?.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Selected dates:
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.specificDates
                          .sort((a, b) => a - b)
                          .map((date, index) => (
                            <div
                              key={index}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-md text-sm"
                            >
                              <Calendar className="h-3 w-3 text-blue-600" />
                              <span className="text-blue-900">
                                {format(date, 'MMM dd, yyyy')}
                              </span>
                              <button
                                onClick={() =>
                                  handleInputChange(
                                    'specificDates',
                                    formData.specificDates.filter((_, i) => i !== index)
                                  )
                                }
                                className="ml-1 rounded-full hover:bg-blue-100 p-0.5 transition-colors"
                              >
                                <X className="h-3 w-3 text-blue-600" />
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {formData.dateType === 'asap' && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-900">
                  Training needed as soon as possible
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-900">{booking?.date_prefs || 'Flexible'}</p>
          )}
        </div>

        <Separator />

        {/* Additional Notes */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Additional Notes</Label>
          {isEditable ? (
            <Textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional information for the training provider..."
              rows={4}
            />
          ) : (
            <p className="text-gray-700 whitespace-pre-wrap">
              {booking?.further_info || <span className="text-gray-500">No additional notes</span>}
            </p>
          )}
        </div>

        <Separator />

        {/* Purchase Order Number - Always editable */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Purchase Order Number</Label>
          <Input
            value={formData.poNumber}
            onChange={(e) => handleInputChange('poNumber', e.target.value)}
            placeholder="Enter PO number (optional)"
          />
          <p className="text-xs text-gray-500">Optional reference for your internal records</p>
        </div>

        {/* Accreditations (read-only) */}
        {booking?.accreditations && booking.accreditations.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="text-sm font-medium">Accreditations</Label>
              <div className="flex flex-wrap gap-2">
                {booking.accreditations.map((acc, idx) => (
                  <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {acc}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right Column - Comments */}
      <div className="lg:border-l lg:pl-6">
        <BookingComments bookingId={booking?.id} />
      </div>
    </div>
  );
};

export default OverviewContent;
